/**
 * Effective-outcome + policy-label presentation helpers.
 *
 * Shared by the mandates LIST card (`Mandates.tsx`) and the DETAIL panel
 * (`MandateDetail.tsx`) so both surfaces agree on a single scannable
 * verdict.
 *
 * WHY this exists (2026-07 UX fix): an APPROVED mandate that was then
 * blocked at settlement — the r31 cross-agent-org-budget gate aborts
 * BEFORE any rail runs (ADR-0151), so no charge is made — used to render
 * TWO contradictory chips: a lime "APPROVED" (the policy verdict) next to
 * a coral "DECLINED" (the settlement outcome). A reviewer could not tell
 * at a glance whether money moved. `effectiveOutcome` collapses the policy
 * decision AND the settlement outcome into ONE headline pill:
 *
 *   approved + succeeded  → CHARGED        (mint)
 *   approved + aborted    → BLOCKED        (coral)  ← r31, no charge
 *   approved + failed     → CHARGE FAILED  (coral)
 *   approved + <none>     → APPROVED       (lime)
 *   rejected family       → REJECTED       (coral)
 *   escalated_approved    → ESCALATED → APPROVED (amber)
 *   escalated_rejected    → ESCALATED → REJECTED (coral)
 *   observed              → OBSERVED       (sky)
 *   handoff_pending        → HANDOFF        (sky)   — ADR-0174; no money
 *                                                      moved, buyer pays on
 *                                                      the merchant's site
 *
 * Settlement only ever exists on an APPROVED, settlement-authorizing
 * mandate, so it never overrides a reject / escalate / observe verdict —
 * those read straight through.
 */

import type { AuditDecision } from '@sill/types';
import type { PillTone } from '../../components/atoms/index.js';

/**
 * Structural subset of the record shape both surfaces carry. The LIST
 * projection (`GET /v1/audit`) and the single-record DETAIL projection
 * (`GET /v1/audit/:record_id`) both surface `settlement.outcome`; the
 * card's existing coral "DECLINED" badge already read it, so no new wire
 * dependency is introduced.
 */
export interface EffectiveOutcomeInput {
  readonly decision: AuditDecision;
  readonly settlement?: {
    readonly rail?: 'stripe' | 'shopify';
    readonly outcome?: string;
    readonly reason?: string;
  };
}

/** A single collapsed verdict pill: policy decision + settlement outcome. */
export function effectiveOutcome(
  record: EffectiveOutcomeInput,
): { tone: PillTone; label: string } {
  switch (record.decision) {
    case 'observed':
      return { tone: 'sky', label: 'OBSERVED' };
    case 'rejected':
    case 'verification_rejected':
    case 'rejected_post_verify':
      return { tone: 'coral', label: 'REJECTED' };
    // ADR-0099 — the escalated terminal states are deliberately surfaced
    // as-is (the human-intervention path matters on an audit product,
    // even on a positive outcome). Left uncollapsed.
    case 'escalated_approved':
      return { tone: 'amber', label: 'ESCALATED → APPROVED' };
    case 'escalated_rejected':
      return { tone: 'coral', label: 'ESCALATED → REJECTED' };
    // ADR-0157 (Bucket B Slice 2) — RAIL-NEUTRAL settlement audit records.
    // These are terminal outcomes bound into the signed audit scope AFTER
    // the ledger row insert; they are NOT the authorizing decision (that
    // ride is `approved` + `settlement.outcome === 'succeeded'`, which
    // falls through to the sub-switch below and returns CHARGED). A
    // `settled_*` record IS the settlement itself, so the headline pill
    // reads as terminal without consulting the sub-outcome:
    //   settled_succeeded → mint SETTLED (money moved, confirmed)
    //   settled_refunded  → coral REFUNDED (money reversed)
    // Per §4 semantic palette: mint=confirmed, coral=reject/reversal.
    case 'settled_succeeded':
      return { tone: 'mint', label: 'SETTLED' };
    case 'settled_refunded':
      return { tone: 'coral', label: 'REFUNDED' };
    // ADR-0174 §1 — the handoff outcome is structurally NOT a settlement:
    // no `settlement_ledger` row, no money moved. Sky = pending, matching
    // `presentDecision`'s widening in MandateDetail.tsx — the two
    // presentation surfaces must agree on the same mandate's headline.
    case 'handoff_pending':
      return { tone: 'sky', label: 'HANDOFF' };
    case 'approved':
      break;
    default: {
      // Exhaustiveness guard (2026-07-09 security review, ADR-0174 Slice 1
      // Major-1). Before this guard, a decision literal added to
      // `AuditDecision` without a case here fell through silently to the
      // settlement sub-switch below and rendered the WRONG pill — exactly
      // what happened to `handoff_pending` for one release (rendered lime
      // APPROVED instead of sky HANDOFF). The compiler now refuses to
      // build if a future literal is left unhandled; the runtime throws
      // rather than silently mislabel a mandate's money-moved status.
      const _exhaustive: never = record.decision;
      throw new Error(
        `effectiveOutcome: unhandled decision "${String(_exhaustive)}" — add a case above, do not let it fall through`,
      );
    }
  }

  // approved — fold the SETTLEMENT outcome into the single headline. No
  // settlement sub-object (rail disabled without a written outcome, or a
  // non-settlement-authorizing approval) falls back to the policy verdict.
  switch (record.settlement?.outcome) {
    case 'succeeded':
      return { tone: 'mint', label: 'CHARGED' };
    case 'failed':
      return { tone: 'coral', label: 'CHARGE FAILED' };
    case 'aborted':
      // A pre-dispatch settlement gate blocked the charge (r31 today writes
      // a rail-less abort). No money moved — coral, distinct from CHARGED.
      return { tone: 'coral', label: 'BLOCKED' };
    default:
      return { tone: 'lime', label: 'APPROVED' };
  }
}

/**
 * Collapse a long generated `policy_version` into a scannable label.
 *
 * Generated policy names bake the active window's timestamps into the
 * string, e.g.
 *   catalog-eligibility-2026-06-15T19-31-16-245Z-2026-06-16T1232-v5
 * which is unreadable in the narrow left column and the detail subline.
 * The slug is an internal mint artifact (it never appears on the Rules
 * page), so only the publish number survives:
 *   → "Rules · version 5"   (the exact id stays on the signed record)
 *
 * The two sentinel versions keep their existing human strings.
 */
export function shortPolicyLabel(version: string): string {
  if (version === 'policy_absent') return 'none active · observe-only';
  if (version === 'policy_eval_failed') return 'rule check unavailable';
  if (version === 'v1-default') return 'Default rules';

  // The stored version is an internal id minted by whichever path last
  // published the rules (`catalog-eligibility-<timestamp>` from the catalog
  // activation, `<template>-vN` from a template, `…-vN` from a Rules-page
  // re-publish). Merchants never see those slugs on the Rules page, so the
  // label names the thing they do see: their rules, and which publish.
  const versionMatch = version.match(/-v(\d+)$/);
  return versionMatch ? `Rules · version ${versionMatch[1]}` : 'Rules';
}
